import dayjs from 'dayjs';
import Highcharts, { PlotScatterOptions, AxisLabelsFormatterContextObject, PlotColumnOptions, Point } from 'highcharts';
import { useLogs } from 'hooks/api/useLogs';
import { Document } from '@extrahorizon/javascript-sdk';
import { t } from 'i18next';
import { HeartRhythmMeasurement } from 'models/heartRhythm/measurement';
import { useParams } from 'react-router-dom';
import { formatDateHighcharts, getHighchartsDateTimeLabelFormats } from 'utils/dates';
import { getLogColor } from 'utils/logs';
import { defaultMeasurementIndicators, getHeartrateValue, getHeartRhythmIndicatorColor } from 'utils/measurement';
import { MedicalEventData, MedicalEventTreatmentMethod, MedicalEventType } from 'models/patientMedicalEvent';
import {
  convertChartValueToSeverity,
  convertSeveritytoChartValue,
  logTooltipContent,
} from 'pages/Main/Patients/Details/Overview/HeartRateOverview/HeartRateCharts/HeartRateCharts.helpers';
import { useAppContext } from 'context/hooks';
import { Indicator } from 'utils/models';

interface Options {
  startDate: Date | null;
  endDate: Date | null;
  onClick?: (item: HeartRhythmMeasurement) => void;
  measurements?: HeartRhythmMeasurement[];
}

Highcharts.SVGRenderer.prototype.symbols.emptySquare = (
  x: number,
  y: number,
  width: number,
  height: number,
): Highcharts.SVGPathArray => {
  return [['M', x, y], ['L', x + width, y], ['L', x + width, y + height], ['L', x, y + height], ['Z']];
};

const LOG_HEIGHT_DIFFERENCE_TO_MEASUREMENT_MAX_VALUE = 20;

const calculateLogHeight = (value: number) => {
  if (value > 0) return value + LOG_HEIGHT_DIFFERENCE_TO_MEASUREMENT_MAX_VALUE;
  return LOG_HEIGHT_DIFFERENCE_TO_MEASUREMENT_MAX_VALUE;
};

export const HeartRateScatterOptions = ({ startDate, endDate, onClick, measurements }: Options) => {
  const highestHeartRateValue = Math.max(...(measurements?.map((obj) => obj?.data?.heartrate || 0) || []));
  const { patientId } = useParams<{ patientId: string }>();
  const { data: logs } = useLogs({ patientId, startDate, endDate });
  const { currentUser } = useAppContext();

  const shouldDisplayLogPlotlines = () => {
    return [
      {
        plotLines: logs?.data.map((log) => {
          return {
            color: getLogColor(
              log.data.type === MedicalEventType.Treatment
                ? log.data.treatment?.method
                : MedicalEventType.DiagnosticResult,
            ),
            width: 3,
            value: dayjs(log.data.loggedTimestamp).unix() * 1000,
            zIndex: 3,
          };
        }),
        plotBands: logs?.data?.map((e) => {
          if (
            e.data.treatment?.method === MedicalEventTreatmentMethod.CatheterAblation &&
            e.data.treatment?.procedure?.blankingPeriodInWeeks
          ) {
            return {
              color: 'var(--mantine-color-chartPlotLines-1)',
              from: dayjs(e.data.loggedTimestamp),
              to: dayjs(e.data.loggedTimestamp).add(e.data.treatment.procedure?.blankingPeriodInWeeks, 'weeks'),
              label: {
                text: t('add_log_blanking_period'),
                align: 'center',
                style: {
                  color: 'gray',
                },
              },
            };
          }
          return {};
        }),
      },
    ];
  };

  const shouldDisplayLogSeries = () => {
    return [
      {
        name: 'Catheter Ablation',
        data: logs?.data?.map((log) => {
          if (log.data.treatment?.method === MedicalEventTreatmentMethod.CatheterAblation) {
            return {
              x: dayjs(log.data.loggedTimestamp).unix() * 1000,
              y: calculateLogHeight(highestHeartRateValue),
              item: log,
            };
          }
          return null;
        }),
        color: 'var(--mantine-color-chartPlotLines-9)',
        marker: {
          symbol: 'square',
          radius: 5,
        },
      },
      {
        name: 'Cardioversion',
        data: logs?.data?.map((log) => {
          if (log.data.treatment?.method === MedicalEventTreatmentMethod.Cardioversion) {
            return {
              x: dayjs(log.data.loggedTimestamp).unix() * 1000,
              y: calculateLogHeight(highestHeartRateValue),
              item: log,
            };
          }
          return null;
        }),
        color: 'var(--mantine-color-chartPlotLines-7)',
        marker: {
          symbol: 'square',
          radius: 5,
        },
      },
      {
        name: 'Medication',
        data: logs?.data?.map((log) => {
          if (log.data.treatment?.method === MedicalEventTreatmentMethod.Medication) {
            return {
              x: dayjs(log.data.loggedTimestamp).unix() * 1000,
              y: calculateLogHeight(highestHeartRateValue),
              item: log,
            };
          }
          return null;
        }),
        color: 'var(--mantine-color-chartPlotLines-5)',
        marker: {
          symbol: 'square',
          radius: 5,
        },
      },
      {
        name: t('add_log_log_type_diagnostic-result'),
        data: logs?.data?.map((log) => {
          if (log.data.type === MedicalEventType.DiagnosticResult) {
            return {
              x: dayjs(log.data.loggedTimestamp).unix() * 1000,
              y: calculateLogHeight(highestHeartRateValue),
              item: log,
            };
          }
          return null;
        }),
        color: 'var(--mantine-color-chartPlotLines-3)',
        marker: {
          symbol: 'square',
          radius: 5,
        },
      },
    ];
  };

  return {
    title: '',
    chart: {
      type: 'scatter',
      height: 250,
    },
    xAxis: {
      type: 'datetime',
      min: dayjs(startDate).unix() * 1000,
      max: dayjs(endDate).unix() * 1000,
      dateTimeLabelFormats: getHighchartsDateTimeLabelFormats(currentUser?.userConfiguration.data.hourFormat),
      ...shouldDisplayLogPlotlines(),
    },
    yAxis: {
      title: { text: t('heart_rate_label') },
      min: 0,
    },
    credits: { enabled: false },
    series: [
      ...Object.values(defaultMeasurementIndicators).map((indicator) => {
        return {
          name: t(`indicator_label_${indicator}`),
          cursor: 'pointer',
          color: getHeartRhythmIndicatorColor(indicator as Indicator),
          data: measurements
            ?.filter((measurement) => measurement.data.indicator === indicator)
            .map((e) => {
              return {
                x: e.data.measurement_timestamp,
                y: e.data.heartrate === -1 ? 0 : e.data.heartrate,
                item: e,
              };
            }),
          marker: {
            symbol: 'circle',
          },
        };
      }),
      ...shouldDisplayLogSeries(),
    ],
    tooltip: {
      useHTML: true,
      outside: true,
      formatter(): string {
        const point = this as Point & {
          item: HeartRhythmMeasurement | Document<MedicalEventData>;
        };

        const data = point.item?.data;
        if (!data) return '';
        if ('type' in data) {
          return logTooltipContent(currentUser?.userConfiguration.data.hourFormat, data);
        }
        if ('indicator' in data && typeof point.x === 'number' && typeof point.y === 'number') {
          const heartrateLabel = `| ${t('heart_rate_label')}: ${getHeartrateValue(point.y, data.indicator)}`;
          return `${t('date_label')}: ${formatDateHighcharts(point.x, currentUser?.userConfiguration.data.hourFormat, true)} ${heartrateLabel}`;
        }
        return '';
      },
    },
    plotOptions: {
      series: {
        allowPointSelect: true,
        point: {
          events: {
            select(e: { target: { item: HeartRhythmMeasurement } }) {
              const {
                target: { item },
              } = e;
              if (!item) return false;
              if ('indicator' in item.data && onClick) {
                onClick(item);
              }
              return false;
            },
          },
        },
      },
    },
    accessibility: {
      enabled: false,
    },
  } as PlotScatterOptions;
};

export const HeartRateBarOptions = ({ startDate, endDate, measurements }: Options) => {
  const { currentUser } = useAppContext();
  return {
    title: '',
    chart: {
      type: 'lollipop',
      height: 150,
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: getHighchartsDateTimeLabelFormats(currentUser?.userConfiguration.data.hourFormat),
      min: dayjs(startDate).unix() * 1000,
      max: dayjs(endDate).unix() * 1000,
    },
    yAxis: {
      title: { text: t('severity_label') },
      tickPositions: [1, 2, 3, 4, 5],
      labels: {
        formatter(context: AxisLabelsFormatterContextObject) {
          const yAxisLabels: { [key: number]: string } = {
            0: '0',
            1: '1',
            2: '2a',
            3: '2b',
            4: '3',
            5: '4',
          };
          return yAxisLabels[+context.value];
        },
      },
    },
    credits: { enabled: false },
    legend: {
      enabled: false,
    },
    series: [
      {
        name: t('indicator_label_normal'),
        color: 'black',
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: false,
            },
          },
        },
        data: measurements
          ?.filter((measurement) => convertSeveritytoChartValue(measurement.data.context?.symptomSeverity) > 1)
          .map((e) => {
            return [e.data.measurement_timestamp, convertSeveritytoChartValue(e.data.context?.symptomSeverity)];
          }),
      },
    ],
    tooltip: {
      formatter() {
        const point = this as Point;
        if (typeof point.x === 'number' && typeof point.y === 'number') {
          return `${t('date_label')}: ${formatDateHighcharts(point.x, currentUser?.userConfiguration.data.hourFormat, true)} | ${t(
            'severity_label',
          )}: ${convertChartValueToSeverity(point.y)}`;
        }
        return '';
      },
    },
    accessibility: {
      enabled: false,
    },
  } as PlotColumnOptions;
};
